import { createSlice } from "@reduxjs/toolkit";

import type { LedgerReport, VatReport } from "@/types/api";

interface ReportsState {
  guestLedgerByDate: Record<string, LedgerReport>;
  cityLedgerByDate: Record<string, LedgerReport>;
  vatByRange: Record<string, VatReport>; // key = `${from}..${to}`
}

const guestReconciled: LedgerReport = {
  date: "2026-05-13",
  currency: "EUR",
  ledger: "guest",
  opening_balance_minor: 1245000,
  closing_balance_minor: 1113000,
  movements_debit_minor: 691000,
  movements_credit_minor: 823000,
  sfm_reconciled: true,
  sfm_balance_minor: 1113000,
  entries: [
    {
      guest_ref: "Smith, J.",
      transaction_ref: "R-10238",
      debit_minor: 32000,
      credit_minor: 0,
      running_balance_minor: 32000,
      source: { type: "revenue", id: "rev-1" },
    },
    {
      guest_ref: "Smith, J.",
      transaction_ref: "P-10238",
      debit_minor: 0,
      credit_minor: 32000,
      running_balance_minor: 0,
      source: { type: "payment", id: "pay-1" },
    },
    {
      guest_ref: "Garcia, M.",
      transaction_ref: "R-10239",
      debit_minor: 58000,
      credit_minor: 0,
      running_balance_minor: 58000,
      source: { type: "revenue", id: "rev-2" },
    },
    {
      guest_ref: "Garcia, M.",
      transaction_ref: "R-10239",
      debit_minor: 58000,
      credit_minor: 0,
      running_balance_minor: 116000,
      source: { type: "revenue", id: "rev-3" },
    },
    {
      guest_ref: "Garcia, M.",
      transaction_ref: "P-10239",
      debit_minor: 0,
      credit_minor: 116000,
      running_balance_minor: 0,
      source: { type: "payment", id: "pay-2" },
    },
  ],
};

const guestVariance: LedgerReport = {
  ...guestReconciled,
  date: "2026-05-14",
  sfm_reconciled: false,
  sfm_balance_minor: 1108750,
};

const cityReport: LedgerReport = {
  date: "2026-05-13",
  currency: "EUR",
  ledger: "city",
  opening_balance_minor: 540000,
  closing_balance_minor: 740000,
  movements_debit_minor: 200000,
  movements_credit_minor: 0,
  sfm_reconciled: true,
  sfm_balance_minor: 740000,
  entries: [
    {
      guest_ref: "Mickey Travel",
      transaction_ref: "INV-2025-088",
      debit_minor: 200000,
      credit_minor: 0,
      running_balance_minor: 740000,
      source: { type: "revenue", id: "rev-c1" },
    },
  ],
};

const vatApril: VatReport = {
  from: "2026-04-01",
  to: "2026-04-30",
  currency: "EUR",
  sfm_reconciled: true,
  sfm_total_gross_minor: 1_245_788,
  rows: [
    {
      vat_code: "VAT7",
      sfm_nominal: "4000-01",
      description: "Accommodation",
      count: 87,
      net_minor: 1_000_000,
      vat_minor: 70_000,
      eco_tax_minor: 0,
      gross_minor: 1_070_000,
    },
    {
      vat_code: "VAT18",
      sfm_nominal: "4100-01",
      description: "Restaurant & bar",
      count: 142,
      net_minor: 90_000,
      vat_minor: 16_200,
      eco_tax_minor: 0,
      gross_minor: 106_200,
    },
    {
      vat_code: "ECO",
      sfm_nominal: "4900-01",
      description: "Eco-Tax",
      count: 87,
      net_minor: 0,
      vat_minor: 0,
      eco_tax_minor: 19_588,
      gross_minor: 19_588,
    },
  ],
  totals: {
    net_minor: 1_090_000,
    vat_minor: 86_200,
    eco_tax_minor: 19_588,
    gross_minor: 1_245_788,
  },
};

const initialState: ReportsState = {
  guestLedgerByDate: {
    "2026-05-13": guestReconciled,
    "2026-05-14": guestVariance,
  },
  cityLedgerByDate: {
    "2026-05-13": cityReport,
  },
  vatByRange: {
    "2026-04-01..2026-04-30": vatApril,
  },
};

const slice = createSlice({
  name: "reports",
  initialState,
  reducers: {},
});

export const vatKey = (from: string, to: string) => `${from}..${to}`;

export default slice.reducer;
